import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import md5 from 'md5';
import QRCode from 'qrcode';
import header from '../../assets/icm-header.jpg';
import icp from '../../assets/icp-brasil.png';
import {
  getBase64PDFImage,
  getBase64PDFImagePNG,
  prefixo,
  scaleStyles,
  baseStyles,
} from '../../util/utils';
import { separarTituloECorpoAtestado } from '../../util/textoMultilinha';
import { obterImagensPorTipoAtestado } from '../../helpers/documentos/atestados';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const QRCODE_ATESTADO = 'https://icm-marilia.com/atestado/validacao';

export default async function generateAtestadoPDF({
  atestado,
  tipo = 'imprimirNormal',
  assinaturaDigital,
  codFunc,
  isDoctorLoggedIn,
}) {
  const timbrado = tipo === 'imprimirTimbrado' || tipo === 'envioWpp';
  const styles = scaleStyles(baseStyles, 1.0);
  const headerImage = timbrado ? await getBase64PDFImage(header) : null;
  const icpLogoBase64 = await getBase64PDFImagePNG(icp);
  const qrCodeDataUrl = await QRCode.toDataURL(QRCODE_ATESTADO);

  const imagensModelo = obterImagensPorTipoAtestado(atestado?.tipo);
  const imagensBase64 = await Promise.all(
    imagensModelo.map(async img => ({
      ...img,
      dataUrl: await getBase64PDFImagePNG(img.src),
    }))
  );

  const dataHora = new Date()
    .toLocaleString('pt-BR', { hour12: false })
    .replace(',', '');

  const descricao = atestado && atestado.descricao ? atestado.descricao : '';
  const { titulo: tituloAtestado, corpo: corpoTexto } =
    separarTituloECorpoAtestado(descricao);

  const anexosContent = [];
  imagensBase64.forEach((img, index) => {
    if (img.title) {
      anexosContent.push({
        text: img.title,
        alignment: 'center',
        bold: true,
        fontSize: 9,
        margin: [0, index === 0 ? 0 : 12, 0, 4],
        ...(index === 0 ? { pageBreak: 'before' } : {}),
      });
    }
    anexosContent.push({
      image: img.dataUrl,
      width: img.width || 440,
      alignment: 'center',
      margin: [0, 0, 0, 8],
      ...(index === 0 && !img.title ? { pageBreak: 'before' } : {}),
    });
  });

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: headerImage ? [40, 140, 20, 130] : [40, 120, 20, 160],
    header: headerImage
      ? {
          image: headerImage,
          width: 500,
          alignment: 'center',
          margin: [0, 10, 0, 0],
        }
      : undefined,
    content: [
      {
        text: tituloAtestado,
        style: 'title',
        alignment: 'center',
        margin: [0, 20, 0, 20],
      },
      {
        text: corpoTexto || ' ',
        style: 'files',
        margin: [100, 10, 30, 10],
        alignment: 'justify',
      },
      ...anexosContent,
    ],
    footer: function footer(currentPage, pageCount) {
      return {
        margin: [40, 0, 40, 0],
        stack: [
          {
            table: {
              widths: [50, '*', '*', 80],
              body: [
                [
                  assinaturaDigital
                    ? {
                        image: icpLogoBase64,
                        width: 80,
                        alignment: 'left',
                        border: [false, false, false, false],
                      }
                    : {
                        width: 80,
                        text: '',
                        border: [false, false, false, false],
                      },
                  {
                    colSpan: 2,
                    stack: [
                      ...(isDoctorLoggedIn
                        ? [
                            {
                              text: assinaturaDigital
                                ? 'Documento assinado digitalmente'
                                : 'Documento assinado eletronicamente',
                              alignment: 'center',
                              bold: true,
                              fontSize: 10,
                              margin: [0, 0, 0, 0],
                            },
                            {
                              text: `Autenticação: ${md5(
                                JSON.stringify(atestado || '')
                              )}`,
                              alignment: 'center',
                              fontSize: 8,
                              margin: [0, 0, 0, 2],
                            },
                          ]
                        : []),
                      {
                        text: `Médico: ${prefixo(atestado)} ${(atestado &&
                          atestado.nomeMedico) ||
                          ''}`,
                        alignment: 'center',
                        fontSize: 11,
                        bold: true,
                        margin: [0, 0, 0, 0],
                      },
                      {
                        text: `CPF: ${(atestado && atestado.cpf) ||
                          ''} - CRM: ${(atestado && atestado.crm) || ''}${
                          atestado && atestado.rqe
                            ? ` - RQE: ${atestado.rqe}`
                            : ''
                        }`,
                        alignment: 'center',
                        fontSize: 11,
                        bold: true,
                        margin: [0, 0, 0, 2],
                      },
                      ...(isDoctorLoggedIn
                        ? [
                            {
                              text: assinaturaDigital
                                ? '* A assinatura digital deste documento poderá ser verificada em https://validar.iti.gov.br/ *'
                                : '* VALIDAÇÃO: Aponte a câmera do celular usando o QRCode ao lado *',
                              alignment: 'center',
                              fontSize: 8,
                              margin: [0, 0, 0, 0],
                            },
                            {
                              text: `* ATESTADO: ${(atestado &&
                                atestado.codAtestado) ||
                                ''} - PACIENTE: ${(atestado &&
                                atestado.codPaciente) ||
                                ''} *`,
                              alignment: 'center',
                              fontSize: 8,
                              margin: [0, 0, 0, 0],
                            },
                          ]
                        : []),
                      {
                        text: `Emitida por ${codFunc || ''} em ${dataHora}`,
                        alignment: 'center',
                        fontSize: 8,
                        margin: [0, 0, 0, 0],
                      },
                      {
                        text: `Página ${currentPage} de ${pageCount}`,
                        alignment: 'center',
                        fontSize: 8,
                        margin: [0, 0, 0, 0],
                      },
                    ],
                    style: 'content',
                    border: [false, false, false, false],
                  },
                  {},
                  ...(isDoctorLoggedIn
                    ? [
                        qrCodeDataUrl
                          ? {
                              image: qrCodeDataUrl,
                              width: 70,
                              alignment: 'right',
                              border: [false, false, false, false],
                            }
                          : {
                              width: 70,
                              text: '',
                              border: [false, false, false, false],
                            },
                      ]
                    : []),
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0,
              vLineWidth: () => 0,
            },
          },
        ],
      };
    },
    styles,
  };

  return pdfMake.createPdf(docDefinition);
}
